import { Component, HostListener, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ToastGlobalService } from 'src/app/layout/toast-global/toast-global.service';
import { VehicleService } from '../vehicle/service/vehicle.service';
import { VehicleDetailService } from './vehicle-detail.service';
import { TenantState } from 'src/app/store/state/tenant.state';
import { TenantSessionState } from 'src/app/store/state/tenant-session.state';
import { SelectSnapshot } from '@ngxs-labs/select-snapshot';
import { TableFilter } from 'src/app/models/tableFilter';
import { TenantStateService } from 'src/app/shared/services/tenant.state.service';
import { VehicleLayoutService } from '../vehicle-layout/vehicle-layout.service';
import { TranslateService } from '@ngx-translate/core';
import { VehicleState } from 'src/app/models/vehicleState';
import { DynamicDialogService } from 'src/app/dynamic-dialog/dynamic-dialog.service';
import { MotorService } from '../motor/motor.service';
import { Observable, Subject, combineLatest } from 'rxjs';
import { Part } from '../../models/part';
import { Vehicle } from 'src/app/models/vehicle';
import { AppConfigurationService, AppSettings } from 'src/app/shared/services/app-configuration.service';
import { distinctUntilChanged, filter, finalize, map, switchMap, take, takeUntil, tap } from 'rxjs/operators';
import { AuthService } from 'src/app/services/auth.service';
import { MaintainAlert, ViewAlert, ViewOutOfService, ViewVehicle, ViewVehicleLayout, ViewVehicleMaintenance, ViewVehiclePart, ViewVehicleSchedule, ViewVehicleState } from 'src/app/models/constant/right';
import { VehiclePart } from 'src/app/models/vehiclePart';
import { DiagnosticProviderService } from 'src/app/shared/services/diagnostic-provider.service';
import { DiagnosticProviders } from 'src/app/models/constant/providers.enum';
import { FeatureFlagService } from 'src/app/shared/services/feature-flag.service';
import { FeatureFlagEnum } from 'src/app/shared/feature-flags.enum';
import { cloneDeep } from 'lodash';
import { jsonCompare } from 'src/app/shared/handler/common-functions.util';
import { TelematicDriverService } from 'src/app/shared/services/telematic-driver.service';
import { Modules, SpaRoutingService, isModule, isModuleAccessible } from 'src/app/shared/services/spa.routing.service';
import { Module } from 'src/app/models/module';
import { TenantUser } from 'src/app/models/tenantUser';
import { Dictionary } from 'lodash';

import { updateTabIndexRoute, getTabIndex } from 'src/app/shared/handler/common-functions.util';
import { OutOfServiceService, OutOfServiceVehicleResponse, ServiceStatus } from 'src/app/out-of-service/out-of-service.service';
import { DrawerService } from 'src/app/shared/drawer/drawer.service';
import { OutOfServiceDetailComponent } from 'src/app/out-of-service/out-of-service-detail/out-of-service-detail.component';
import { GoogleMapService } from 'src/app/services/google-map.service';
import { FeatureSettings, TenantFeatures, isFeatureEnabled } from 'src/app/models/featureSettings';
import { MatTab } from '@angular/material/tabs';
import { editVehicleLayout, vehicleMapEvent } from '../vehicle-actions';

@Component({
    selector: 'app-vehicle-detail',
    templateUrl: './vehicle-detail.component.html',
    styleUrls: ['./vehicle-detail.component.css'],
    standalone: false
})
export class VehicleDetailComponent implements OnInit, OnDestroy {
  vehicleGroups: Array<any>;
  oosData: OutOfServiceVehicleResponse;
  @HostListener('window:beforeunload')
  canDeactivate(): Observable<boolean> | boolean {
    // returning true will navigate without confirmation
    const vehicleCompareResult = jsonCompare(this.vehicle, this.originalVehicle, ['displayAttributes']);
    const vehicleStateCompareResult =  jsonCompare(this.vehicleState, this.originalVehicleState, ['dashboard', 'driverStatusList', 'sensors']);
    const canDeactivate = this.vehicleConfigComponent.canDeactivate();
    // returning false will show a confirm dialog before navigating away
    return vehicleCompareResult
      && vehicleStateCompareResult
      && canDeactivate;
  }
  @ViewChild('vehicleConfig') vehicleConfigComponent;
  @SelectSnapshot(TenantState.getCurrentTenantExternalId) tenantId: string;
  @SelectSnapshot(TenantSessionState.getTenantFeatures) tenantFeatures: FeatureSettings;
  @SelectSnapshot(TenantSessionState.getTenantModules) tenantModules: Module[];
  @SelectSnapshot(TenantSessionState.getCurrentUser) currentUser: TenantUser;
  @ViewChild('tabGroup') tabGroup;

  get hasMotor(): boolean {
    return isFeatureEnabled(this.tenantFeatures, TenantFeatures.FleetCross);
  }

  // Showing the widget is a separate opt-in from running the Issues pipeline. A tenant can
  // process for weeks before anyone is shown a screen, and a screen can be withdrawn without
  // stopping the pipeline, which would lose the events it never processed.
  //
  // The flag answers for the tenant and the module answers for the reader, so both have to
  // agree. Without the second test an administrator can take Issues off the navigation for
  // a group and every member of that group still meets the widget on every vehicle.
  get isIssuesFeatureEnabled(): boolean {
    return isFeatureEnabled(this.tenantFeatures, TenantFeatures.IssuesUI)
      && this.isIssuesModuleOffered;
  }

  // The same ordinal, rights and groups that decide the navigation entry.
  //
  // An absent module means hidden, not shown. The module list is already filtered per
  // reader before it reaches the client: the query drops a module whose rights the reader
  // does not hold, and one scoped to a group id the reader is not in. So a missing Issues
  // row is the server having answered "not for you", and treating it as "unconfigured, so
  // allow" would show the widget to exactly the readers the gate exists to exclude. The
  // widget is now reachable on the same terms as the navigation entry, which is the point.
  private get isIssuesModuleOffered(): boolean {
    const issues = (this.tenantModules ?? []).find(m => isModule(m, Modules.ISSUES));
    return isModuleAccessible(issues, this.profile?.right, this.currentUser?.groups);
  }

  get canViewOutOfService(): boolean {
    return isFeatureEnabled(this.tenantFeatures, TenantFeatures.EnableOutOfService);
  }

  get useScSensorGrid(): boolean {
    return isFeatureEnabled(this.tenantFeatures, TenantFeatures.VehicleDetailSensorGrid);
  }

  // Tab indices shift with permissions and feature flags — identify tabs by
  // instance, never by index.
  @ViewChild('maintenanceTab') private maintenanceTab?: MatTab;
  @ViewChild('scheduleTab') private scheduleTab?: MatTab;

  vehicle: Vehicle;
  originalVehicle: Vehicle;
  vehicleState: VehicleState;
  originalVehicleState: VehicleState;
  vehicleLayout: any;
  vin: string;
  selectedIndex: any = 0;
  calculatedAvg: number;
  moduleProviderCredentials: any;
  lastFilter = new TableFilter();

  refreshSchedules = 0;
  scheduleChange = false;
  refreshMaintenances = 0;
  maintenanceChange = false;

  currentLanguage: string;
  length: number;
  innerWidth: number;
  direction = 'horizontal';
  configurationError: boolean;
  configurationErrorCount: number;
  hover: boolean;
  hasMitchell1: boolean;
  motorSrc = 'fleetcross299_byMOTOR.png';
  mitchell1Src = 'Mitchell1.png';
  vehicleParts: VehiclePart[];
  assetUrl: string;
  profile: any;
  canViewVehicleState: boolean;
  canViewVehicleLayout: boolean;
  canViewVehicleMaintenance: boolean;
  canViewVehicleSchedule: boolean;
  canViewVehicleParts: boolean;
  canViewAlert: boolean;
  canMaintainAlert: boolean;
  canViewVehicleGroup: boolean;
  telematicDriver$: Observable<Dictionary<string>>;
  destroy$ = new Subject();
  loading: boolean = false;
  ServiceStatus: typeof ServiceStatus = ServiceStatus;

  constructor(
    private vehicleService: VehicleService,
    private vehicleDetailService: VehicleDetailService,
    private telematicDriverService: TelematicDriverService,
    private router: Router,
    private route: ActivatedRoute,
    private toastGlobalService: ToastGlobalService,
    private tenantStateService: TenantStateService,
    private vehicleLayoutService: VehicleLayoutService,
    public translate: TranslateService,
    public dialogService: DynamicDialogService,
    private motor: MotorService,
    private readonly diagnosticProviderService: DiagnosticProviderService,
    private readonly appConfigurationService: AppConfigurationService,
    private readonly authService: AuthService,
    private featureFlagService: FeatureFlagService,
    private readonly oosService: OutOfServiceService,
    private readonly drawerService: DrawerService,
    private googleMapService: GoogleMapService,
    private readonly spaRoutingService: SpaRoutingService) {
      this.googleMapService.addScriptToHeader().subscribe();
  }

  @HostListener('window:resize', ['$event'])
  onResize(event): void {
    this.innerWidth = window.innerWidth;
    this.direction = this.innerWidth >= 400 ? 'horizontal' : 'vertical';
  }
  
  ngOnInit(): void {
    this.appConfigurationService.settings$
      .pipe(take(1))
      .subscribe((settings: AppSettings) => {
        this.assetUrl = this.appConfigurationService.settings.contentDeliveryNetworkUrl;
      });

    if (location.search.includes('tabIndex')) {
      this.selectedIndex = getTabIndex(location.search);
    }
    if (location.search.includes('index')) {
      const urlParams = new URLSearchParams(window.location.search);
      this.selectedIndex = urlParams.get('index');
    }
    this.route.paramMap.pipe(
      map(params => params.get('id')),
      filter((id): id is string => !!id),
      distinctUntilChanged(),
      takeUntil(this.destroy$)
    ).subscribe(vin => {
      this.vin = vin;
      this.getData();
    });
  }

  getData(): void {
    this.loading = true;
    this.vehicleService.getVehicleByVinAndTenant(this.vin, this.tenantId)
      .pipe(
        finalize(() => this.loading = false),
        takeUntil(this.destroy$)
      )
      .subscribe(vehicle => {
        this.vehicle = vehicle;
        this.originalVehicle = cloneDeep(this.vehicle);
        this.vehicleGroups = cloneDeep(this.vehicle.groups);
        this.vehicleGroups.map(group => {
          let tempArray = [];
          Object.keys(group.values).forEach(key => {
            tempArray.push({ key: key, value: group.values[key] });
          });
          group.displayValues = tempArray;
          group.expanded = false;
        });
        this.updateConfiguration();
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.complete();
  }

  // Group attribute values that are boolean (or "true"/"false" strings) render
  // as a check/x icon instead of text. Returns null for non-boolean values.
  openGroup(groupId: string): void {
    this.spaRoutingService.navigateToGroup(groupId);
  }

  groupValueBool(value: any): 'true' | 'false' | null {
    if (value === true) { return 'true'; }
    if (value === false) { return 'false'; }
    if (typeof value === 'string') {
      const v = value.trim().toLowerCase();
      if (v === 'true') { return 'true'; }
      if (v === 'false') { return 'false'; }
    }
    return null;
  }
 

  updateConfiguration(): void {
    this.configurationError = false;
    this.configurationErrorCount = 0;
    if (this.vehicle.buildConfiguration.driveType === ''
      && this.vehicle.buildConfiguration.vehicleType?.toLowerCase() !== 'trailer') {
      this.configurationError = true;
      this.configurationErrorCount += 1;
    }

    this.diagnosticProviderService.findDiagnosticProvider(this.tenantId)
      .subscribe((providers: string[]) => {
        this.hasMitchell1 = providers.some(p => p.toLowerCase() === DiagnosticProviders.Mitchell)
          && this.vehicle.groups.filter(
            x => (x.values.IsMitchell1 ?? x.values.isMitchell1 ?? '')
              .toLowerCase() === 'true').length > 0;
      });

    this.hydrate();
  }
  onTabClick(event: any): void {

    updateTabIndexRoute(event.index, this.router, this.route);

    if (this.maintenanceChange && event.tab === this.maintenanceTab) {
      this.maintenanceChange = false;
      this.refreshMaintenances++;
    }

    if (this.scheduleChange && event.tab === this.scheduleTab) {
      this.scheduleChange = false;
      this.refreshSchedules++;
    }

  }



  hydrate(): void {
    this.vehicleParts = [];
    this.profile = this.authService.getClaims();
    // module.rights.filter(x => profile.right.includes(x)).length > 0;
    if (this.profile.right.includes(ViewVehicleState)) {
      this.canViewVehicleState = true;
      this.getVehicleStateByVin();
    }
    if (this.profile.right.includes(ViewVehicleLayout)) {
      this.canViewVehicleLayout = true;
      this.getVehicleLayout();
    }
    if (this.profile.right.includes(ViewVehiclePart)) {
      this.canViewVehicleParts = true;
      this.getVehicleParts();
    }
    if (this.profile.right.includes(ViewVehicleMaintenance)) {
      this.canViewVehicleMaintenance = true;
    }
    if (this.profile.right.includes(ViewVehicleSchedule)) {
      this.canViewVehicleSchedule = true;
    }
    if (this.profile.right.includes(ViewAlert)) {
      this.canViewAlert = true;
    }
    if (this.profile.right.includes(MaintainAlert)) {
      this.canMaintainAlert = true;
    }
    if (this.profile.right.includes(ViewVehicle)) {
      this.canViewVehicleGroup = true;
    }
    if(this.profile.right.includes(ViewOutOfService) && this.canViewOutOfService){
      this.oosService.getOutOfServiceVehicleByVin(this.vin)
        .pipe(
          map(oosData => this.oosData = oosData))
        .subscribe();
    }
  }

  

  getVehicleLayout(): void {
    this.vehicleLayoutService.resolveLayoutForVehicle(this.vehicle)
      .subscribe(layout => this.vehicleLayout = layout ?? {});
  }

  openVehicleMap(): void {
    const event = vehicleMapEvent(this.vehicleState, this.translate, this.toastGlobalService);
    if (event) {
      this.callbackFunction(event);
    }
  }

  openVehicleLayoutDialog(): void {
    editVehicleLayout(this.dialogService, this.vehicle, this.vehicleLayout, e => this.callbackFunction(e));
  }

  getVehicleStateByVin(): void {
    this.vehicleDetailService.getVehicleStateByVin(
      this.vehicle.vin)
      .subscribe(
        rsp => {
          this.vehicleState = rsp;
          this.originalVehicleState = cloneDeep(this.vehicleState);
          this.setDisplayValues();
          this.telematicDriver$ = this.telematicDriverService.getDriversByVehicleState(this.vehicleState);
        },
        error => {
          this.vehicleState = new VehicleState();
          this.originalVehicleState = cloneDeep(this.vehicleState);
        });
  }

  getVehicleParts(): void {
    this.vehicleService.getVehicleParts(this.vehicle.vin).subscribe(res => {
      this.vehicleParts = res;
    });
  }

  setDisplayValues(): void {
    this.vehicleState.dashboard.indicators.forEach(lamp => {
      if (lamp) {
        this.vehicleState.dashboard[lamp.name] = lamp.enabled;
      }
    });
  }

  removeParts(event: any): void {
    this.vehicleDetailService.removeVehicleParts(event.rowsSelected).subscribe(res => {
      this.hydrate();
      const toast = this.translate.instant('@updateSuccessful');
      this.toastGlobalService.show(toast, { classname: 'success' });
    });
  }

  editPart(currentPart: Part): void {
    const payload = {
      part: currentPart,
      layout: this.vehicleLayout,
      showMassAdd: false
    };
    this.dialogService.openAddPartDialog(payload).afterClosed().subscribe(res => {
      if(res){
        currentPart.location = res.location;
        currentPart.vehicles[0].partQuantity = res.quantity;
        const partsToAdd = new Array();
        partsToAdd.push(currentPart);
        this.postParts(partsToAdd);
      }
    });
  }

  postParts(partsToAdd: any): void {
    this.vehicleDetailService.postVehicleParts(partsToAdd).subscribe(res => {
      this.hydrate();
      const toast = this.translate.instant('@updateSuccessful');
      this.toastGlobalService.show(toast, { classname: 'success' });
    });
  }

  callbackFunction(event: any): void {
    switch (event.action) {
      case 'openMap':
        const options = {
          center: { lat: event.vehicleState.position.latitude, lng: event.vehicleState.position.longitude },
          zoom: 15
        };
        const markerPositions = [{ lat: event.vehicleState.position.latitude, lng: event.vehicleState.position.longitude }];
        const payload = { options, markerPositions };
        this.dialogService.openMapDialog(payload).afterClosed().subscribe(res => {

        });
        break;
      case 'refreshMaintenances':
        this.maintenanceChange = true;
        break;
      case 'refreshSchedules':
        this.scheduleChange = true;
        break;
      case 'updateVehicle':
        this.vehicleService.updateVehicle(event.vehicle)
        .pipe(
          switchMap(() => this.vehicleService.getVehicleByVinAndTenant(event.vehicle.vin, this.tenantId)),
          tap((vehicle: Vehicle) => {
            this.vehicle = vehicle;
            this.originalVehicle = cloneDeep(this.vehicle);
            this.hydrate();
          })
        ).subscribe(() => {
          const toast = this.translate.instant('@updateSuccessful');
          this.toastGlobalService.show(toast, { classname: 'success' });
        });
        break;
      case 'vehicleUpdated':
        this.originalVehicle = cloneDeep(this.vehicle);
        break;
    }
  }

  openFleetCrossDialog(): void {
    combineLatest([
      this.motor.getUrl(),
      this.featureFlagService.getFeatureFlagValue(FeatureFlagEnum.MotorNewTab)
        .pipe(take(1))
    ]).subscribe(([res, shouldOpenInNewTab]) => {
      const url = res.url + '&VIN=' + this.vin;
      if (shouldOpenInNewTab) {
        window.open(url, "_blank");
      } else {
        const dialog = this.dialogService.openFleetCrossDialog(url);
        dialog.afterClosed().subscribe();
      }
    });
  }

  openMitchell(): void {
    this.diagnosticProviderService.getMitchellUrl(
      this.tenantId,
      this.vin,
      'J1939',
      '0',
      '0',
      '',
      '0'
    ).subscribe(rsp => {
      const dialog = this.dialogService.openFleetCrossDialog(rsp.url);
      dialog.afterClosed().subscribe(res2 => {
        // do nothing
      });
    },
      error => {

      });
  }
  outOfServiceBannerClicked(){
    const detailComponent = this.drawerService.open(
      OutOfServiceDetailComponent,
      [
        { input: "vehicleData", value: this.oosData },
        { input: "hideVehicleDetailsButton", value: true }
      ],
      {
        resizable: true,
      }
    );
    detailComponent.closed
      .subscribe(() => {
        this.drawerService.close();
      });
  }
 
}
